import { useState, useEffect } from 'react';
import {
  ApolloProvider,
  ApolloClient,
  HttpLink,
  ApolloLink,
  InMemoryCache,
  concat,
  from,
} from '@apollo/client';
import { CachePersistor, LocalStorageWrapper } from 'apollo3-cache-persist';
import removeAuthCredentials from '../Auth/removeAuthCredentials';
// import { reportError } from '@dietlabs/utils';
import { onError } from '@apollo/client/link/error';
import Loader from '../Layout/Loader';

const httpLink = new HttpLink({
  uri: import.meta.env.VITE_REACT_APP_GRAPHQL_URL,
  credentials: 'same-origin',
  headers: {
    'X-AppEnvironment': import.meta.env.VITE_REACT_APP_ENV,
    'X-AppVersion': `ReactWebApp/${process.env.VITE_APP_VERSION}`,
  },
});

// eslint-disable-next-line import/no-mutable-exports
export let networkErrorVar;

const errorLink = onError(({ networkError, graphQLErrors }) => {
  if (graphQLErrors) {
    graphQLErrors.map(
      ({ message, locations, path }) =>
        console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
      // reportError(
      //     `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      // )
    );
  }
  if (networkError) {
    const newError = new Error('Network error', 'Failed to fetch');
    newError.networkError = networkError;
    networkErrorVar = newError;

    // Unauthorized
    if (networkError.statusCode === 401) {
      removeAuthCredentials();
    }
  }
});

const authMiddleware = new ApolloLink((operation, forward) => {
  // add the authorization to the headers and set dynamic api uri
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      'X-Authentication': headers['X-Authentication'] || localStorage?.getItem('token') || null,
      'X-Locale': localStorage?.getItem('i18nextLng')?.replace('-', '_') || 'en_US',
      'X-RequestClientTime': new Date().toISOString(),
      'X-TimeZone': Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    uri:
      localStorage.getItem('developerOptionsApiUri') ||
      `${import.meta.env.VITE_REACT_APP_GRAPHQL_URL}?${operation.operationName}`,
  }));

  return forward(operation);
});

const appLink = from([errorLink, concat(authMiddleware, httpLink)]);

const Apollo = ({ children }) => {
  const [client, setClient] = useState(null);

  useEffect(() => {
    async function init() {
      const cache = new InMemoryCache({
        typePolicies: {
          UserShoppingListCategoryProduct: {
            keyFields: ['key'],
          },
          UserHydrationDay: {
            keyFields: ['key'],
          },
          UserDiet: {
            keyFields: ['id'],
          },
          UserDailyDietPlanType: {
            keyFields: ['id'],
          },
          UserDietSet: {
            keyFields: ['dietSetId'],
          },
          UserDailyDietPlanEventMeal: {
            keyFields: ['key'],
          },
          UserDailyDietMeal: {
            keyFields: ['key'],
          },
          UserActivity: {
            keyFields: ['id'],
          },
          UserDailyDietMealDishIngredient: {
            keyFields: ['key'],
          },
          UserMeasurement: {
            keyFields: [],
          },
          Diabetes: {
            keyFields: [],
          },
          BloodSugarMeasurementQuery: {
            keyFields: [],
          },
          Treatment: {
            keyFields: [],
          },
          Cookbook: {
            keyFields: [],
          },
          CookbookIngredient: {
            keyFields: ['key'],
          },
          DietDishIngredient: {
            keyFields: ['productId'],
          },
          DailyTrackerQuery: {
            keyFields: [],
          },
          DailyTrackerData: {
            keyFields: ['date'],
          },
        },
      });

      let newPersistor = new CachePersistor({
        cache,
        storage: new LocalStorageWrapper(window.localStorage),
        debug: false,
        trigger: 'write',
        debounce: 0,
      });

      await newPersistor.restore();

      setClient(
        new ApolloClient({
          cache,
          link: appLink,
        })
      );
    }

    init().catch(console.error);
  }, []);

  if (!client) {
    return (
      <div className="full-height">
        <Loader />
      </div>
    );
  }

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default Apollo;
